import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { WhatsAppMediaAudio } from './WhatsAppMediaAudio';
import { transcribeWhatsAppMessage } from './whatsAppMessageThunks';
import type { WhatsAppMessage } from './whatsAppMessageTypes';

/**
 * WHATSAPP VOICE NOTE — nota vocale: player audio più il tasto di trascrizione.
 *
 * Si distingue a colpo d'occhio da un file audio allegato (icona microfono), ed è
 * l'unico media trascrivibile: un mp3 allegato può essere musica, e trascriverlo
 * sarebbe una chiamata a pagamento buttata.
 *
 * La trascrizione, una volta ottenuta, sta in DB: chi riapre la chat la rilegge senza
 * ripagarla. Per questo il tasto sparisce appena c'è un `transcriptionDate`, e il
 * testo prende il suo posto.
 */
interface WhatsAppVoiceNoteProps {
  message: WhatsAppMessage;
}

export function WhatsAppVoiceNote({ message }: WhatsAppVoiceNoteProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // La trascrizione dura secondi: chiudere l'accordion durante l'attesa smonterebbe il
  // componente, e il setState nel .finally arriverebbe su un componente morto.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Trascritto = c'è la data, non "c'è del testo": un audio senza parole potrebbe
  // legittimamente produrre una trascrizione vuota, e non va richiesto di nuovo.
  const transcribed = Boolean(message.transcriptionDate);

  const handleTranscribe = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void dispatch(transcribeWhatsAppMessage(message.whatsAppMessageId))
      .unwrap()
      .catch(() => {
        if (mounted.current) setError(t('whatsapp.voice.transcribeError'));
      })
      .finally(() => {
        if (mounted.current) setBusy(false);
      });
  };

  return (
    <div className="wa-voice">
      <div className="wa-voice__head">
        <span aria-hidden="true">🎤</span>
        <span className="wa-voice__label">{t('whatsapp.voice.label')}</span>
      </div>

      <WhatsAppMediaAudio message={message} />

      {!transcribed && (
        <button
          type="button"
          className="wa-voice__transcribe"
          onClick={handleTranscribe}
          disabled={busy}
        >
          {busy
            ? t('whatsapp.voice.transcribing')
            : t('whatsapp.voice.transcribe')}
        </button>
      )}

      {error && <span className="wa-voice__error">{error}</span>}

      {transcribed && <WhatsAppTranscript message={message} />}
    </div>
  );
}

/**
 * Il testo della nota vocale, con riassunto e action items se il motore li ha
 * prodotti. Sono capability indipendenti e possono fallire da sole: le sezioni vuote
 * non si disegnano, invece di mostrare intestazioni senza contenuto.
 *
 * Aperto di default: chi ha appena premuto Trascrivi vuole leggere, non cliccare
 * ancora.
 */
function WhatsAppTranscript({ message }: WhatsAppVoiceNoteProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const actionItems = (message.transcriptionActionItems ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className="wa-transcript">
      <button
        type="button"
        className="wa-transcript__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {t('whatsapp.transcription.title')}
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="wa-transcript__body">
          {message.transcription ? (
            <p className="wa-transcript__text">{message.transcription}</p>
          ) : (
            <p className="wa-transcript__empty">
              {t('whatsapp.transcription.empty')}
            </p>
          )}

          {message.transcriptionSummary && (
            <div className="wa-transcript__section">
              <span className="wa-transcript__label">
                {t('whatsapp.transcription.summary')}
              </span>
              <p className="wa-transcript__text">
                {message.transcriptionSummary}
              </p>
            </div>
          )}

          {actionItems.length > 0 && (
            <div className="wa-transcript__section">
              <span className="wa-transcript__label">
                {t('whatsapp.transcription.actionItems')}
              </span>
              <ul className="wa-transcript__actions">
                {actionItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WhatsAppVoiceNote;
